import React from "react";
import { AlertTriangle, X } from "lucide-react";
import { apiAlasanReject, apiGetProductionQueueDetail, apiLaporMasalah, formatApiErrorDetail } from "@/lib/api";
import { cekLapor, jalurUlang, LAINNYA, posTujuanPilihan, teksAlasan } from "@/lib/laporMasalah";

const inputCls = "rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200";
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

// v2.17 (owner 2026-10-22, requirement 75 butir 9d): any station (not QC — it keeps its own Reject) sends
// work back to an earlier station: target station, item/qty, a quick reason (the Owner's list) + a note,
// then a confirmation. Works like a QC reject: the stations from the target up to this one run again.
export default function LaporMasalahPanel({ q, onClose, onDone }) {
  const [d, setD] = React.useState(null);
  const [alasanList, setAlasanList] = React.useState([]);
  const [f, setF] = React.useState({ tujuan: "", alasan: "", keterangan: "", qty: "", item: "", perVariasi: {} });
  const [yakin, setYakin] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  React.useEffect(() => {
    (async () => {
      try {
        const [det, al] = await Promise.all([apiGetProductionQueueDetail(q.spk_id), apiAlasanReject()]);
        setD(det); setAlasanList(al);
        const st = det.workflow_instances.find((w) => w.instance_id === q.instance_id)?.steps || [];
        const pilih = posTujuanPilihan(st, q.urutan);
        setF((x) => ({ ...x, tujuan: pilih.length ? String(pilih[pilih.length - 1].urutan) : "", item: det.items?.length === 1 ? "0" : "" }));
      } catch (e) { setError(errText(e)); }
    })();
  }, [q.spk_id, q.instance_id, q.urutan]);
  if (!d) {
    return <div data-testid="lapor-form" className="rounded-xl border border-orange-500/40 bg-slate-900/60 p-4 text-sm text-slate-400">{error || "Memuat…"}</div>;
  }
  const steps = d.workflow_instances.find((w) => w.instance_id === q.instance_id)?.steps || [];
  const pilihan = posTujuanPilihan(steps, q.urutan);
  const jersey = d.jenis_transaksi === "JERSEY";
  const baris = jersey && !q.bagian_id && (d.jahit_baris || []).length > 1 ? d.jahit_baris : [];
  const st = steps.find((s) => s.urutan === q.urutan);
  const maks = jersey ? (q.bagian_id ? q.bagian_pcs : st?.qty_rework?.qty || d.jersey?.total_pcs) : Number(d.items?.[Number(f.item)]?.quantity) || null;
  const label = alasanList.find((a) => a.kode === f.alasan)?.label;
  const kirim = async () => {
    const salah = (!jersey && f.item === "" ? "Pilih item yang bermasalah." : null)
      || cekLapor({ alasan: f.alasan, keterangan: f.keterangan, qty: Number(f.qty), maks, baris, perVariasi: f.perVariasi });
    if (salah) { setError(salah); return; }
    if (!yakin) { setYakin(true); setError(null); return; }
    setBusy(true); setError(null);
    try {
      const qv = Object.fromEntries(Object.entries(f.perVariasi).filter(([, n]) => Number(n) > 0).map(([v, n]) => [v, Number(n)]));
      await apiLaporMasalah(q.spk_id, q.instance_id, q.urutan, {
        pos_tujuan_urutan: Number(f.tujuan), alasan_kode: f.alasan, keterangan: f.keterangan.trim() || null, qty: Number(f.qty),
        ...(jersey ? {} : { item_index: Number(f.item) }), ...(baris.length ? { qty_per_variasi: qv } : {}),
        ...(q.bagian_id ? { bagian_id: q.bagian_id } : {}) });
      onDone();
    } catch (e) { setError(errText(e)); setYakin(false); } finally { setBusy(false); }
  };
  const ubah = (patch) => { setF({ ...f, ...patch }); setYakin(false); };
  return (
    <div data-testid="lapor-form" className="rounded-xl border border-orange-500/40 bg-slate-900/60 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-100"><AlertTriangle size={18} className="text-orange-400" /> Lapor masalah — {q.nomor_spk}</h2>
        <button data-testid="lapor-tutup" onClick={onClose} className="text-slate-400 hover:text-slate-200"><X size={18} /></button>
      </div>
      <p className="text-sm text-slate-400">Pekerjaan dikembalikan ke pos sebelumnya untuk diperbaiki. Pos itu sampai pos Anda mengerjakan ulang; alasan tampil di antrean pos tujuan dan dibacakan di layar produksi.</p>
      <label className="block text-xs text-slate-400">Dikembalikan ke pos *
        <select data-testid="lapor-tujuan" value={f.tujuan} onChange={(e) => ubah({ tujuan: e.target.value })} className={`block ${inputCls}`}>
          {pilihan.map((s) => <option key={s.urutan} value={s.urutan}>{s.pos_nama || s.pos_code}</option>)}
        </select></label>
      <div>
        <div className="mb-1 text-xs text-slate-400">Alasan * (pilih satu)</div>
        <div data-testid="lapor-alasan" className="flex flex-wrap gap-2">
          {alasanList.map((a) => (
            <button key={a.kode} type="button" data-testid={`lapor-alasan-${a.kode}`} onClick={() => ubah({ alasan: a.kode })}
              className={`min-h-[40px] rounded-lg border px-3 text-sm ${f.alasan === a.kode ? "border-orange-500 bg-orange-500 font-semibold text-slate-950" : "border-slate-600 text-slate-200 hover:border-orange-400"}`}>{a.label}</button>
          ))}
        </div>
      </div>
      <label className="block text-xs text-slate-400">Keterangan{f.alasan === LAINNYA ? " * (minimal 5 huruf)" : " (boleh kosong)"}
        <textarea data-testid="lapor-keterangan" rows={2} maxLength={300} value={f.keterangan} onChange={(e) => ubah({ keterangan: e.target.value })}
          placeholder="contoh: biru jadi ungu di bagian lengan" className={`block w-full ${inputCls}`} /></label>
      <div className="flex flex-wrap gap-3">
        {!jersey && (
          <label className="text-xs text-slate-400">Item *
            <select data-testid="lapor-item" value={f.item} onChange={(e) => ubah({ item: e.target.value })} className={`block ${inputCls}`}>
              <option value="">— pilih item —</option>
              {(d.items || []).map((it, i) => <option key={i} value={i}>{it.produk || it.jenis_pekerjaan || it.kategori_produk} ({it.quantity} {it.satuan || "pcs"})</option>)}
            </select></label>
        )}
        <label className="text-xs text-slate-400">Jumlah bermasalah *{maks ? ` (maks ${maks}${jersey ? " pcs" : ""})` : ""}
          <input data-testid="lapor-qty" type="number" min="1" max={maks || undefined} value={f.qty} onChange={(e) => ubah({ qty: e.target.value })} className={`block w-28 ${inputCls}`} /></label>
      </div>
      {baris.length > 0 && (
        <div data-testid="lapor-variasi" className="flex flex-wrap gap-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
          {baris.map((b) => (
            <label key={b.variasi_no} className="text-xs text-slate-400">Variasi {b.variasi_no}{b.nama ? ` · ${b.nama}` : ""} (maks {b.pcs})
              <input data-testid={`lapor-variasi-${b.variasi_no}`} type="number" min="0" max={b.pcs} value={f.perVariasi[b.variasi_no] ?? ""}
                onChange={(e) => ubah({ perVariasi: { ...f.perVariasi, [b.variasi_no]: e.target.value } })} className={`block w-24 ${inputCls}`} /></label>
          ))}
        </div>
      )}
      <p data-testid="lapor-alur" className="text-xs text-amber-200">Yang mengerjakan ulang: {jalurUlang(steps, f.tujuan, q.urutan).join(" → ")}{q.bagian_id ? " (bagian Anda berkurang, pcs perbaikan kembali sebagai bagian baru)" : ""}</p>
      {error && <div data-testid="lapor-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>}
      {yakin && (
        <div data-testid="lapor-konfirmasi" className="rounded-lg border border-orange-500/50 bg-orange-500/10 px-3 py-2 text-sm text-orange-800">
          Kirim laporan: <b>{f.qty} {jersey ? "pcs" : ""}</b> ke pos <b>{pilihan.find((s) => String(s.urutan) === f.tujuan)?.pos_nama}</b> — alasan <b>{teksAlasan(label, f.alasan, f.keterangan)}</b>? Tekan sekali lagi untuk mengirim.
        </div>
      )}
      <div className="flex gap-2">
        <button data-testid="lapor-kirim" disabled={busy} onClick={kirim} className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-orange-500 px-5 text-sm font-semibold text-slate-950 hover:bg-orange-400 disabled:opacity-50">
          <AlertTriangle size={15} /> {yakin ? "Ya, kirim laporan" : "Lapor masalah"}</button>
        <button data-testid="lapor-batal" onClick={onClose} className="min-h-[44px] rounded-lg border border-slate-600 px-4 text-sm text-slate-300">Batal</button>
      </div>
    </div>
  );
}

// v2.17: the reporter's own open report (while the earlier stations redo the pieces / after they are done)
export function LaporanNote({ lap, testid }) {
  if (!lap) return null;
  return (
    <span data-testid={testid} className="mt-1 block rounded border border-orange-500/40 bg-orange-500/10 px-1.5 py-0.5 text-[11px] font-sans text-orange-800">
      <b>Laporan Anda</b> ke {lap.pos_rework_nama || lap.pos_rework}: {lap.alasan}{lap.qty_reject ? ` (${lap.qty_reject} ${(lap.satuan || "pcs").toLowerCase()})` : ""} · {lap.perbaikan_selesai ? "perbaikan selesai, lanjutkan" : "menunggu perbaikan"}
    </span>
  );
}
